import React, { useState, useRef, useEffect } from "react";
import {
  Avatar,
  Button,
  TextField,
  Box,
  IconButton,
  Paper,
} from "@mui/material";
import { BasePhotoModal } from "./BasePhotoModal";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import PersonIcon from "@mui/icons-material/Person";
import BadgeIcon from "@mui/icons-material/Badge";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import { VehicleManagementSection } from "./vehicles/VehicleManagementSection";

export const GuestPhotosSection = ({
  onPhotoUpdate,
  initialPhotos,
  existingInvitado,
  onVehiclesUpdate,
  onVehiclePatenteChange,
  personaid,
}) => {
  const [vehicles, setVehicles] = useState([]);

  const [showModal, setShowModal] = useState({
    profile: false,
    dni: false,
    license: false,
    vehicle: false,
  });

  const [photos, setPhotos] = useState({
    profile: "",
    dni: "",
    license: "",
    vehicle: "",
  });

  const handleVehiclesUpdate = (updatedVehicles) => {
    setVehicles(updatedVehicles);
    onVehiclesUpdate(updatedVehicles);
  };

  useEffect(() => {
    if (initialPhotos) {
      // creates a new object with the same structure
      const newPhotos = {
        profile: initialPhotos.profile || "",
        dni: initialPhotos.dni || "",
        license: initialPhotos.license || "",
        vehicle: initialPhotos.vehicle || "",
      };

      setPhotos(newPhotos);
    }
  }, [initialPhotos]);

  const scrollContainerRef = useRef(null);

  const handleShowModal = (type) => {
    setShowModal({ ...showModal, [type]: true });
  };

  const handleCloseModal = (type) => {
    setShowModal({ ...showModal, [type]: false });
  };

  const handleSavePhoto = (type, photo) => {
    const newPhotos = { ...photos, [type]: photo };
    setPhotos(newPhotos);
    onPhotoUpdate(newPhotos);
  };

  const handleFileChange = (type, e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const newPhotos = { ...photos, [type]: reader.result };
        setPhotos(newPhotos);
        onPhotoUpdate(newPhotos);
      };
      reader.readAsDataURL(file);
    }
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  //f different placeholder icons for each section
  const getPlaceholderIcon = (type) => {
    const iconProps = {
      sx: {
        width: "60%",
        height: "60%",
        color: "#bdbdbd",
      },
    };

    switch (type) {
      case "profile":
        return <PersonIcon {...iconProps} />;
      case "dni":
        return <BadgeIcon {...iconProps} />;
      case "license":
        return <CreditCardIcon {...iconProps} />;
      case "vehicle":
        return <DirectionsCarIcon {...iconProps} />;
      default:
        return <PersonIcon {...iconProps} />;
    }
  };

  const PhotoSection = ({ type, title }) => (
    <Paper
      elevation={2}
      sx={{
        minWidth: "250px",
        p: 2,
        m: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Avatar
        sx={{
          width: 120,
          height: 120,
          borderRadius: "8px",
          border: photos[type] ? "2px solid #4caf50" : "2px dashed #ccc",
          bgcolor: photos[type] ? "transparent" : "#f5f5f5",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
        src={photos[type]}
        alt={`Foto ${title}`}
        imgProps={{
          onError: (e) => {
            console.error(`Error loading ${type} image:`, e);
          },
        }}
      >
        {!photos[type] && getPlaceholderIcon(type)}
      </Avatar>
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={() => handleShowModal(type)}
        sx={{ width: "100%" }}
      >
        {photos[type] ? "Cambiar" : "Tomar"} foto de {title}
      </Button>
      <TextField
        variant="outlined"
        type="file"
        size="small"
        onChange={(e) => handleFileChange(type, e)}
        sx={{
          width: "100%",
          "& .MuiOutlinedInput-root": {
            fontSize: "0.875rem",
          },
        }}
      />
      <BasePhotoModal
        show={showModal[type]}
        handleClose={() => handleCloseModal(type)}
        picture={photos[type]}
        setPicture={(photo) => handleSavePhoto(type, photo)}
        onSave={(photo) => handleSavePhoto(type, photo)}
        title={`Foto de ${title}`}
      />
    </Paper>
  );

  return (
    <Box sx={{ position: "relative", mb: 4 }}>
      <IconButton
        onClick={() => scroll("left")}
        sx={{
          position: "absolute",
          left: -20,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 2,
          bgcolor: "background.paper",
          boxShadow: 2,
          "&:hover": { bgcolor: "background.paper" },
        }}
      >
        <ArrowBackIosNewIcon />
      </IconButton>

      <Box
        ref={scrollContainerRef}
        sx={{
          display: "flex",
          overflowX: "auto",
          scrollbarWidth: "none", // Firefox
          "&::-webkit-scrollbar": {
            // Chrome, Safari
            display: "none",
          },
          px: 2,
          mx: 2,
        }}
      >
        <PhotoSection type="profile" title="Perfil" />
        <PhotoSection type="dni" title="DNI" />
        <PhotoSection type="license" title="Licencia" />
        <VehicleManagementSection
          existingInvitado={existingInvitado}
          onVehiclesUpdate={handleVehiclesUpdate}
          onVehiclePatenteChange={onVehiclePatenteChange}
          personaid={personaid}
          initialVehicles={vehicles}
        />
      </Box>

      <IconButton
        onClick={() => scroll("right")}
        sx={{
          position: "absolute",
          right: -20,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 2,
          bgcolor: "background.paper",
          boxShadow: 2,
          "&:hover": { bgcolor: "background.paper" },
        }}
      >
        <ArrowForwardIosIcon />
      </IconButton>
    </Box>
  );
};
